import { useForm, FormProvider } from "react-hook-form";
import { getFontAwesomeIcon } from "../../../../../../utils";
import { useApprovalsModalViewModel } from "../../viewModels/useApprovalsModalViewModel";
import { useApprovalDecisionsViewModel } from "../../viewModels/useApprovalDecisionsViewModel";
import { motion, AnimatePresence } from "framer-motion";

// COMPONENTS
import {
	BaseButton,
	BaseInput,
	BaseToolTip,
	PrimaryButton,
	SecondaryButton,
	LoadingMessage,
} from "../../../../../../components";

// TYPES
import { FormValues } from "../../../../types";
import { BaseModalProps } from "../../../../types";

const SingleApprovalDecisionForm = <T,>({
	modalSelected,
	onClose,
	fields,
	updateRowData,
	parentModalClose,
}: BaseModalProps<T>) => {
	//// HOOKS ////
	const { approvalDecisions, activeUuid } = useApprovalsModalViewModel();

	const { createApprovalDecisionMutation, isLoadingCreateDecision } =
		useApprovalDecisionsViewModel({
			approvalDecisions,
			decisionType: modalSelected,
			fields,
			activeUuid: activeUuid as string,
			updateRowData,
			parentModalClose,
		});

	/////////////////////////////////////////////////////////////////// FORM SETUP ////////////////////////////////////////////////////////////////////
	const methods = useForm<FormValues>({
		mode: "onChange",
		defaultValues: {
			reason: "",
		},
	});

	const { handleSubmit, watch } = methods;

	const reason = watch("reason");

	const onSubmit = async (formData: Record<string, any>) => {
		await createApprovalDecisionMutation.mutateAsync(formData);

		parentModalClose && parentModalClose();
	};

	//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

	let isConfirmButtonDisabled = false;
	let confirmButtonTooltipMessage = "";
	if (modalSelected === "deny" && !reason) {
		isConfirmButtonDisabled = true;
		confirmButtonTooltipMessage = "Please enter a reason";
	} else {
		isConfirmButtonDisabled = false;
	}

	const confirmButton = (
		<PrimaryButton
			text={fields?.confirmButton.label}
			additionalClasses="ml-5"
			icon={getFontAwesomeIcon(
				fields?.title.prefix,
				fields?.title.prefixWeight,
			)}
			iconBreakpoint="max-sm:pr-0"
			as="button"
			textBreakpoint="max-sm:hidden"
			iconClasses="pr-2 text-white"
			onClick={handleSubmit(onSubmit)}
			isDisabled={isConfirmButtonDisabled}
			disabledButtonClasses="bg-gray-400"
		/>
	);

	const loadingContainerClasses = "flex items-center justify-center w-full min-h-[270px]";

	if (isLoadingCreateDecision) {
		return (
			<motion.div
				initial={{ opacity: 1 }}
				animate={{ opacity: 1 }}
				exit={{ opacity: 0 }}
				transition={{
					duration: 0.4,
					ease: "easeInOut",
				}}
				className={loadingContainerClasses}
			>
				<div className="flex items-center h-full">
					<LoadingMessage message={""} />
				</div>
			</motion.div>
		);
	}
	return (
		<FormProvider {...methods}>
			<form onSubmit={handleSubmit(onSubmit)}>
				<div className="flex flex-col justify-center  w-full gap-5">
					<div className="flex flex-col gap-2">
						<div className="w-full h-full flex flex-col">
							{fields?.reasonTextBox?.isVisible && (
								<BaseInput field={fields?.reasonTextBox} />
							)}
						</div>
						<div className="flex justify-end">
							<SecondaryButton
								type="button"
								text={fields?.cancelButton.label}
								additionalClasses="border-1 border-primary px-8"
								onClick={onClose}
							/>
							{isConfirmButtonDisabled ? (
								<BaseToolTip
									message={confirmButtonTooltipMessage}
									toolTipPlacement="top"
									children={confirmButton}
								/>
							) : (
								confirmButton
							)}
						</div>
					</div>
				</div>
			</form>
		</FormProvider>
	);
};

export default SingleApprovalDecisionForm;
